import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { IndianRupee, FileText, MessageSquare, Briefcase, Building2, Send, Image as ImageIcon, Upload, Youtube, Trash2, Download } from "lucide-react";
import { api, getUser } from "../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Admin() {
  const nav = useNavigate();
  const user = getUser();
  const [overview, setOverview] = useState(null);
  const [donations, setDonations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [careers, setCareers] = useState([]);
  const [csr, setCsr] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [bulkLog, setBulkLog] = useState([]);
  const [media, setMedia] = useState([]);
  const [bulk, setBulk] = useState({ channel: "twilio_whatsapp", recipients: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [vidForm, setVidForm] = useState({ title: "", youtube_url: "", description: "" });

  useEffect(() => {
    if (!user || user.role !== "admin") { nav("/login"); return; }
    loadAll();
    // eslint-disable-next-line
  }, []);

  const loadAll = async () => {
    try {
      const [o, d, c, ca, cs, n, b, m] = await Promise.all([
        api.get("/admin/overview"), api.get("/admin/donations"), api.get("/admin/contacts"),
        api.get("/admin/careers"), api.get("/admin/csr"), api.get("/admin/newsletter"),
        api.get("/admin/bulk-messages"), api.get("/admin/media"),
      ]);
      setOverview(o.data); setDonations(d.data); setContacts(c.data);
      setCareers(ca.data); setCsr(cs.data); setNewsletter(n.data);
      setBulkLog(b.data); setMedia(m.data);
    } catch { toast.error("Unable to load admin data"); }
  };

  const sendBulk = async (e) => {
    e.preventDefault(); setSending(true);
    try {
      const recipients = bulk.recipients.split(/[,\n]/).map(s => s.trim()).filter(Boolean);
      if (!recipients.length) { toast.error("Add at least one recipient"); setSending(false); return; }
      const { data } = await api.post("/admin/bulk-message", { channel: bulk.channel, recipients, subject: bulk.subject, message: bulk.message });
      toast.success(`Queued ${data.total} message(s) via ${bulk.channel} (MOCKED)`);
      const b = await api.get("/admin/bulk-messages"); setBulkLog(b.data);
      setBulk({ ...bulk, recipients: "", subject: "", message: "" });
    } catch { toast.error("Bulk send failed"); } finally { setSending(false); }
  };

  const uploadMedia = async (e, kind) => {
    e.preventDefault();
    const file = e.target.elements.file.files[0];
    const title = e.target.elements.title.value;
    const description = e.target.elements.description.value;
    if (!file) return toast.error("Pick a file");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("kind", kind); fd.append("title", title); fd.append("description", description);
      await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" }});
      toast.success(`${kind === "photo" ? "Photo" : "Document"} uploaded`);
      e.target.reset();
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const addVideo = async (e) => {
    e.preventDefault();
    if (!vidForm.title || !vidForm.youtube_url) return toast.error("Title & YouTube URL required");
    try {
      await api.post("/admin/media/video", vidForm);
      toast.success("Video added");
      setVidForm({ title: "", youtube_url: "", description: "" });
      loadAll();
    } catch { toast.error("Failed to add video"); }
  };

  const delMedia = async (id) => {
    if (!window.confirm("Delete this media item?")) return;
    try { await api.delete(`/admin/media/${id}`); toast.success("Deleted"); loadAll(); } catch { toast.error("Delete failed"); }
  };

  if (!user || user.role !== "admin") return null;

  const photos = media.filter(m => m.kind === "photo");
  const videos = media.filter(m => m.kind === "video");
  const documents = media.filter(m => m.kind === "document");

  return (
    <div className="fade-in max-w-7xl mx-auto px-4 md:px-6 py-10">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#FF9933] font-bold mb-1">Admin</div>
          <h1 className="serif text-3xl md:text-4xl font-semibold text-[#000080]">Control Centre</h1>
          <p className="text-sm text-[#737373] mt-1">Signed in as {user.email}</p>
        </div>
      </div>

      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
          <Stat icon={IndianRupee} label="Total Raised" value={`₹${overview.total_raised.toLocaleString("en-IN")}`} color="#FF9933" />
          <Stat icon={FileText} label="Donations" value={overview.donations_success} color="#138808" />
          <Stat icon={MessageSquare} label="Contacts" value={overview.contacts_count} color="#000080" />
          <Stat icon={Briefcase} label="Careers" value={overview.careers_count} color="#FF9933" />
          <Stat icon={Building2} label="CSR" value={overview.csr_count} color="#138808" />
          <Stat icon={ImageIcon} label="Media" value={overview.media_count} color="#000080" />
        </div>
      )}

      <Tabs defaultValue="donations" className="bg-white rounded-2xl border border-[#eae5d9] p-4 md:p-6">
        <TabsList className="bg-[#F3EFE7] mb-5 flex-wrap h-auto">
          <TabsTrigger value="donations" data-testid="tab-donations">Donations</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts</TabsTrigger>
          <TabsTrigger value="careers" data-testid="tab-careers">Careers</TabsTrigger>
          <TabsTrigger value="csr" data-testid="tab-csr">CSR</TabsTrigger>
          <TabsTrigger value="newsletter" data-testid="tab-newsletter">Newsletter</TabsTrigger>
          <TabsTrigger value="bulk" data-testid="tab-bulk">Bulk Message</TabsTrigger>
          <TabsTrigger value="gallery" data-testid="tab-gallery">Gallery</TabsTrigger>
          <TabsTrigger value="docs" data-testid="tab-docs">Documents</TabsTrigger>
        </TabsList>

        <TabsContent value="donations">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-[#F3EFE7]">
                {["Date","Receipt #","Donor","Email","Purpose","Amount","Method","Status","Receipt"].map(c => <th key={c} className="text-left px-3 py-2.5 text-xs uppercase tracking-wider text-[#737373] font-bold">{c}</th>)}
              </tr></thead>
              <tbody>
                {donations.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-[#737373]">No donations yet</td></tr>}
                {donations.map(d => (
                  <tr key={d.id} className="border-b border-[#eae5d9] hover:bg-[#FDFBF7]">
                    <td className="px-3 py-2.5">{fmt(d.created_at)}</td>
                    <td className="px-3 py-2.5 font-mono text-xs">{d.receipt_no || "—"}</td>
                    <td className="px-3 py-2.5">{d.donor_name}</td>
                    <td className="px-3 py-2.5">{d.donor_email}</td>
                    <td className="px-3 py-2.5">{d.purpose}</td>
                    <td className="px-3 py-2.5 font-semibold text-[#FF9933]">₹{d.amount}</td>
                    <td className="px-3 py-2.5">{d.payment_method}</td>
                    <td className="px-3 py-2.5"><span className={`text-xs px-2 py-0.5 rounded-full ${d.status === "success" ? "bg-[#138808]/10 text-[#138808]" : "bg-[#737373]/10 text-[#737373]"}`}>{d.status}</span></td>
                    <td className="px-3 py-2.5">
                      {d.status === "success" && (
                        <a href={`${BACKEND_URL}/api/donations/${d.id}/receipt`} target="_blank" rel="noreferrer" data-testid={`donation-receipt-${d.id}`}>
                          <Button size="sm" variant="outline" className="rounded-full"><Download className="w-3 h-3 mr-1" />PDF</Button>
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
        <TabsContent value="contacts">
          <Table cols={["Date","Name","Email","Subject","Message"]} rows={contacts.map(c => [fmt(c.created_at), c.name, c.email, c.subject, c.message?.slice(0,80)])} />
        </TabsContent>
        <TabsContent value="careers">
          <Table cols={["Date","Name","Email","Role","Experience","Phone"]} rows={careers.map(c => [fmt(c.created_at), c.name, c.email, c.role, `${c.experience_years}y`, c.phone])} />
        </TabsContent>
        <TabsContent value="csr">
          <Table cols={["Date","Company","Contact","Email","Interest"]} rows={csr.map(c => [fmt(c.created_at), c.company_name, c.contact_person, c.email, c.program_interest])} />
        </TabsContent>
        <TabsContent value="newsletter">
          <Table cols={["Subscribed","Email"]} rows={newsletter.map(n => [fmt(n.subscribed_at), n.email])} />
        </TabsContent>

        <TabsContent value="bulk">
          <div className="grid md:grid-cols-5 gap-6">
            <form onSubmit={sendBulk} className="md:col-span-2 space-y-3" data-testid="bulk-form">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#737373] block mb-1.5">Channel</label>
                <Select value={bulk.channel} onValueChange={(v) => setBulk({ ...bulk, channel: v })}>
                  <SelectTrigger data-testid="bulk-channel"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="twilio_whatsapp">WhatsApp (Twilio)</SelectItem>
                    <SelectItem value="meta_whatsapp">WhatsApp (Meta Cloud)</SelectItem>
                    <SelectItem value="email">Email (Resend)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Textarea placeholder="Recipients (comma or newline)" value={bulk.recipients} onChange={(e) => setBulk({ ...bulk, recipients: e.target.value })} className="min-h-[100px]" data-testid="bulk-recipients" />
              {bulk.channel === "email" && <Input placeholder="Subject" value={bulk.subject} onChange={(e) => setBulk({ ...bulk, subject: e.target.value })} data-testid="bulk-subject" />}
              <Textarea required placeholder="Your message..." value={bulk.message} onChange={(e) => setBulk({ ...bulk, message: e.target.value })} className="min-h-[140px]" data-testid="bulk-message" />
              <Button type="submit" disabled={sending} className="w-full bg-[#FF9933] hover:bg-[#E68A2E] text-white h-11 rounded-full btn-glow" data-testid="bulk-send">
                <Send className="w-4 h-4 mr-2" />{sending ? "Sending..." : "Send Bulk (MOCKED)"}
              </Button>
              <p className="text-[11px] text-[#737373] bg-[#F3EFE7] p-2 rounded">MOCKED: Requests are logged. Add Twilio / Meta / Resend keys in .env to activate real delivery.</p>
            </form>
            <div className="md:col-span-3">
              <div className="text-xs font-bold uppercase tracking-wider text-[#737373] mb-2">Send history</div>
              <Table cols={["Date","Channel","Recipient","Status"]} rows={bulkLog.map(l => [fmt(l.created_at), l.channel, l.recipient, l.status])} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="gallery">
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <UploadForm label="Upload Photo" kind="photo" accept="image/*" onSubmit={(e) => uploadMedia(e, "photo")} busy={uploading} icon={Upload} testid="upload-photo" />
            <form onSubmit={addVideo} className="bg-white rounded-2xl border border-[#eae5d9] p-5 space-y-3" data-testid="add-video-form">
              <div className="flex items-center gap-2 mb-1"><Youtube className="w-5 h-5 text-[#FF9933]" /><div className="font-bold text-[#000080]">Add YouTube Video</div></div>
              <Input placeholder="Title" value={vidForm.title} onChange={(e) => setVidForm({ ...vidForm, title: e.target.value })} data-testid="video-title" />
              <Input placeholder="YouTube URL" value={vidForm.youtube_url} onChange={(e) => setVidForm({ ...vidForm, youtube_url: e.target.value })} data-testid="video-url" />
              <Input placeholder="Description (optional)" value={vidForm.description} onChange={(e) => setVidForm({ ...vidForm, description: e.target.value })} data-testid="video-desc" />
              <Button type="submit" className="w-full bg-[#000080] hover:bg-[#000066] text-white rounded-full h-10" data-testid="video-add">Add Video</Button>
            </form>
          </div>

          <div className="text-xs font-bold uppercase tracking-wider text-[#737373] mt-6 mb-2">Photos ({photos.length})</div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-6">
            {photos.map(p => (
              <div key={p.id} className="relative group aspect-square rounded-xl overflow-hidden bg-[#F3EFE7]">
                <img src={`${BACKEND_URL}${p.public_url}`} alt={p.title} className="w-full h-full object-cover" />
                <button onClick={() => delMedia(p.id)} className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded opacity-0 group-hover:opacity-100"><Trash2 className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
          <div className="text-xs font-bold uppercase tracking-wider text-[#737373] mt-6 mb-2">Videos ({videos.length})</div>
          <div className="grid md:grid-cols-3 gap-3">
            {videos.map(v => (
              <div key={v.id} className="relative group bg-white rounded-xl border border-[#eae5d9] overflow-hidden">
                {v.thumbnail_url && <img src={v.thumbnail_url} alt={v.title} className="w-full aspect-video object-cover" />}
                <div className="p-3">
                  <div className="text-sm font-semibold text-[#000080] truncate">{v.title}</div>
                  <button onClick={() => delMedia(v.id)} className="text-xs text-red-500 mt-1"><Trash2 className="w-3 h-3 inline mr-1" />Remove</button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="docs">
          <div className="mb-6">
            <UploadForm label="Upload PDF Document" kind="document" accept="application/pdf" onSubmit={(e) => uploadMedia(e, "document")} busy={uploading} icon={FileText} testid="upload-doc" />
          </div>
          <div className="text-xs font-bold uppercase tracking-wider text-[#737373] mb-2">Uploaded Documents ({documents.length})</div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {documents.map(d => (
              <div key={d.id} className="bg-white rounded-xl border border-[#eae5d9] p-4">
                <FileText className="w-6 h-6 text-[#FF9933] mb-2" />
                <div className="font-semibold text-[#000080]">{d.title}</div>
                <div className="text-xs text-[#737373] mt-1 truncate">{d.original_filename}</div>
                <div className="flex gap-2 mt-3">
                  <a href={`${BACKEND_URL}${d.public_url}`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="rounded-full">View</Button></a>
                  <Button size="sm" onClick={() => delMedia(d.id)} variant="outline" className="rounded-full text-red-500 border-red-200"><Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function UploadForm({ label, kind, accept, onSubmit, busy, icon: Icon, testid }) {
  return (
    <form onSubmit={onSubmit} className="bg-white rounded-2xl border border-[#eae5d9] p-5 space-y-3" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1"><Icon className="w-5 h-5 text-[#FF9933]" /><div className="font-bold text-[#000080]">{label}</div></div>
      <Input name="title" placeholder="Title" required data-testid={`${testid}-title`} />
      <Input name="description" placeholder="Description (optional)" data-testid={`${testid}-desc`} />
      <input name="file" type="file" accept={accept} required className="block w-full text-sm text-[#4A4A4A] file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#FF9933]/10 file:text-[#FF9933] file:font-semibold" data-testid={`${testid}-file`} />
      <Button type="submit" disabled={busy} className="w-full bg-[#FF9933] hover:bg-[#E68A2E] text-white rounded-full h-10" data-testid={`${testid}-submit`}>
        <Upload className="w-4 h-4 mr-2" />{busy ? "Uploading..." : "Upload"}
      </Button>
    </form>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-2xl border border-[#eae5d9] p-5">
      <Icon className="w-5 h-5 mb-3" strokeWidth={1.6} style={{ color }} />
      <div className="serif text-2xl font-semibold text-[#000080]">{value}</div>
      <div className="text-xs uppercase tracking-wider text-[#737373] mt-1">{label}</div>
    </div>
  );
}

function fmt(d) { try { return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }); } catch { return ""; } }

function Table({ cols, rows }) {
  if (!rows || rows.length === 0) return <div className="text-sm text-[#737373] p-6 text-center">No records yet.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="bg-[#F3EFE7]">
          {cols.map(c => <th key={c} className="text-left px-3 py-2.5 text-xs uppercase tracking-wider text-[#737373] font-bold">{c}</th>)}
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-[#eae5d9] hover:bg-[#FDFBF7]">
              {r.map((cell, j) => <td key={j} className="px-3 py-2.5 text-[#1A1A1A]">{cell ?? "—"}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
